<template>
  <div v-if="isFetching" class="loading-wrapper">
    <loader />
  </div>
  <div
    v-else-if="getAiVisibilitySubscription && getAiVisibilitySubscription.status !== 'canceled'"
    class="ai-visibility-content"
  >
    <div class="categories">
      <div class="header">
        <span class="title">
          {{ $t('domains') }}
        </span>
      </div>
      <div v-if="getAiVisibilityDomainsList" class="domains-list">
        <div v-for="item in getList" :key="item.id" class="domain-item">
          <router-link class="domain-link" :to="`/ai-visibility/${item.id}`">
            <span class="text">{{ item.domain }}</span>
          </router-link>
          <kw-button
            :button-classes="'delete-btn'"
            button-type="text"
            prepend-icon-class="kw kw-trash-2"
            @click="openDeleteModal(item.id)"
          />
        </div>
      </div>
    </div>
    <div class="ai-footer">
      <progress-block
        :title="$t('used-credits')"
        :tooltipTitle="$t('credits-used')"
        :value="getAiVisibilityCredits.used"
        :max="getAiVisibilityCredits.limit"
        color="#782BC7"
      />
      <p class="get-more-btn link" @click="openSubscribeModal(true)">Get more AI Search Credits</p>
      <kw-button
        :button-text="$t('add-domain')"
        :button-classes="'rankscale'"
        button-type="primary"
        prepend-icon-class="kw kw-plus"
        @click="openAddDomain"
      />
    </div>
  </div>
  <unlock-ai-visibility @openSubscribeModal="openSubscribeModal(false)" v-else />
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import ProgressBlock from './ProgressBlock.vue';
import AddAiVisibilityDomain from '@/components/modals/AddAiVisibilityDomain.vue';
import DeleteAiVisibilityDomain from '@/components/modals/DeleteAiVisibilityDomain.vue';
import UnlockAiVisibility from './UnlockAiVisibility.vue';
import SubscribeToAiVisibilityModal from '@/components/modals/SubscribeToAiVisibilityModal.vue';
import { beforeModalOpen, beforeModalClose } from '@/helpers/modalHelpers';
import { Events, EventBus } from '@/events';
import { isAiPackage, isAiSubscriptionAlive } from '~/views/auth/billing-utils';

export default {
  name: 'AiVisibilityDomains',
  components: {
    'progress-block': ProgressBlock,
    'unlock-ai-visibility': UnlockAiVisibility,
  },
  props: {
    search: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      loading: false,
    };
  },
  created() {
    this.fetchPackages();
    EventBus.on(Events.AI_VISIBILITY_EMPTY_DOMAIN_LIST, this.openAddDomain);
  },
  beforeDestroy() {
    EventBus.off(Events.AI_VISIBILITY_EMPTY_DOMAIN_LIST, this.openAddDomain);
  },
  computed: {
    ...mapGetters([
      'getCurrentPlan',
      'getAiVisibilityCredits',
      'getAiVisibilitySubscription',
      'getAiVisibilityDomainsList',
      'getUserDataProp',
      'getUserData',
      'getPackages',
    ]),
    isFetching() {
      return !this.getUserDataProp('email');
    },
    getList() {
      if (!this.getAiVisibilityDomainsList) return [];
      return this.getAiVisibilityDomainsList.filter((item) => item.domain.includes(this.search));
    },
  },
  mounted() {
    this.fetchList();
  },
  watch: {
    getAiVisibilitySubscription(to) {
      if (to) {
        this.fetchList();
      }
    },
  },
  methods: {
    ...mapActions(['fetchAiVisibilityDoaminsList', 'fetchPackages']),
    openAddDomain(title = null) {
      this.$modal.show(
        AddAiVisibilityDomain,
        {
          title: typeof title === 'string' ? title : null,
        },
        {
          classes: 'v--modal',
          height: 'auto',
          width: document.body.offsetWidth < 649 ? '90%' : 475,
          name: 'AddAiVisibilityDomain',
        },
      );
    },
    openDeleteModal(id) {
      this.$modal.show(
        DeleteAiVisibilityDomain,
        {
          domainId: id,
        },
        {
          classes: 'v--modal',
          height: 'auto',
          width: document.body.offsetWidth < 400 ? '90%' : 371,
          name: 'AddAiVisibilityDomain',
        },
      );
    },
    openSubscribeModal(isUpdate) {
      if (Math.random() >= 0 || Array.isArray(this.getUserData._original.default_card)) {
        const aiPackage = this.getPackages.find(isAiPackage);
        if (aiPackage) {
          this.$router.push({
            name: 'AccountDetails',
            query: { id: aiPackage.package_identity },
          });
          return;
        }
      }
      this.$modal.show(
        SubscribeToAiVisibilityModal,
        {
          update: isUpdate,
        },
        {
          classes: 'v--modal v-mobile-custom-scroll',
          height: 'auto',
          width: document.body.offsetWidth < 649 ? '90%' : 640,
          name: 'SubscribeToAiVisibilityModal',
        },
        {
          'before-open': beforeModalOpen,
          'before-close': beforeModalClose,
        },
      );
    },
    fetchList() {
      if (!this.getAiVisibilityDomainsList && isAiSubscriptionAlive(this.getAiVisibilitySubscription)) {
        this.loading = true;
        this.fetchAiVisibilityDoaminsList().finally(() => {
          this.loading = false;
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.ai-visibility-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding-top: 3px;
  padding-right: 14px;
}
.categories {
  flex: 1;
  display: flex;
  flex-direction: column;
  .title {
    color: #909399;
    letter-spacing: 1px;
    font-size: 14px;
    padding-bottom: 4px;
  }
  .domains-list {
    flex: 1 1 auto;
    overflow-y: auto;
    .domain-item {
      width: 100%;
      display: inline-flex;
      justify-content: space-between;
      align-items: center;
      border-radius: 4px;
      overflow: hidden;
      position: relative;
      a {
        display: inline-block;
        padding: 4px 8px;
        width: 100%;
        text-decoration: none;
        color: kw-color(kw-black, 1);
        .text {
          display: inline-block;
          overflow: hidden;
          width: 100%;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-weight: 400;
          font-size: 14px;
          line-height: 24px;
          letter-spacing: -1%;
          vertical-align: middle;
        }
      }
      .delete-btn {
        position: absolute;
        right: 4px;
        display: none;
        width: 16px;
        height: 16px;
        ::v-deep .kw-trash-2 {
          font-weight: 900;
          color: kw-color(kw-purple, 11);
        }
      }
      .router-link-active,
      &:hover {
        background-color: #f1e6ff;
        .delete-btn {
          display: inline-block;
        }
      }
      &:has(.router-link-active) {
        .delete-btn {
          display: inline-block;
        }
      }
    }
  }
}
.ai-footer {
  .get-more-btn {
    cursor: pointer;
  }
  .link {
    display: inline-block;
    font-family: Roboto;
    font-weight: 400;
    font-size: 12px;
    line-height: 10px;
    color: kw-color(kw-purple, 10);
    margin: 8px 0;
  }
  & > button.kw-button.kw-btn-primary {
    height: 44px;
    width: 100%;
  }
}
.loading-wrapper {
  width: 100%;
  height: 200px;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
